import type {
  AbstractPartDefinition,
  Blueprint,
  DragConstraint,
  DragUpdateOptions,
  DragUpdateResult,
  ProductSettings,
} from "@project/emerge-types";
import cloneDeep from "lodash/cloneDeep";
import type { Vector3 } from "three";
import type { Download } from "../core/Download";
import { stableFingerprint } from "../util/dependencyFingerprint";
import type { AbstractBuildstep } from "./AbstractBuildstep";
import type { AbstractPartInteraction } from "./AbstractPartInteraction";

export interface ProductBuildStateSnapshot<
  SettingsType extends ProductSettings<string>,
  BlueprintType extends Blueprint,
  PartDefinitionType extends AbstractPartDefinition<string>,
> {
  settings: SettingsType;
  blueprint: BlueprintType | undefined;
  partDefinitions: Map<string, PartDefinitionType>;
  dependencyCacheKeys: Map<string, Set<string>>;
  dependencyFingerprints: Map<string, Map<string, string>>;
}

const restoredDependencyMarker = {};

function cloneNestedKeySet<Value>(
  source: Map<string, Map<string, Value>>,
): Map<string, Set<string>> {
  return new Map(
    Array.from(source.entries(), ([key, values]) => [
      key,
      new Set(values.keys()),
    ]),
  );
}

function cloneNestedMap<Value>(
  source: Map<string, Map<string, Value>>,
): Map<string, Map<string, Value>> {
  return new Map(
    Array.from(source.entries(), ([key, values]) => [key, new Map(values)]),
  );
}

function restoreNestedMap<Value>(
  target: Map<string, Map<string, Value>>,
  snapshot: Map<string, Map<string, Value>>,
): void {
  target.clear();
  for (const [key, values] of snapshot.entries()) {
    target.set(key, new Map(values));
  }
}

function restoreDependencyCache(
  target: Map<string, Map<string, unknown>>,
  snapshotKeys: Map<string, Set<string>>,
): void {
  target.clear();
  for (const [key, partIds] of snapshotKeys.entries()) {
    target.set(
      key,
      new Map(
        Array.from(partIds, (partId) => [partId, restoredDependencyMarker]),
      ),
    );
  }
}

/** @deprecated Prefer stableFingerprint(a) === stableFingerprint(b). */
export function areDependenciesEqual(dep1: unknown, dep2: unknown): boolean {
  return stableFingerprint(dep1) === stableFingerprint(dep2);
}

/**
 * AbstractProductDefinition represents a vendor‑agnostic product.
 * It holds the configuration settings, a generated blueprint, and part definitions.
 */
export abstract class AbstractProductDefinition<
  SettingsType extends ProductSettings<string> = ProductSettings<string>,
  BlueprintType extends Blueprint = Blueprint,
  PartDefinitionType extends
    AbstractPartDefinition<string> = AbstractPartDefinition<string>,
> {
  public settings: SettingsType;
  public blueprint?: BlueprintType;
  // Changed from array to Map for efficient lookup by ID
  public partDefinitions: Map<string, PartDefinitionType> = new Map();
  // Cache for the new build pattern
  private readonly dependencyCache: Map<string, Map<string, unknown>> =
    new Map();
  private readonly dependencyFingerprints: Map<string, Map<string, string>> =
    new Map();

  // Buildsteps and part interactions should be defined in derived classes
  protected buildSteps: AbstractBuildstep<
    BlueprintType,
    SettingsType,
    PartDefinitionType
  >[] = [];
  protected partInteractions: AbstractPartInteraction<
    BlueprintType,
    SettingsType,
    PartDefinitionType
  >[] = [];

  constructor(settings: SettingsType) {
    this.settings = settings;
  }

  public createBuildStateSnapshot(): ProductBuildStateSnapshot<
    SettingsType,
    BlueprintType,
    PartDefinitionType
  > {
    return {
      settings: this.settings,
      blueprint: this.blueprint,
      partDefinitions: this.partDefinitions,
      dependencyCacheKeys: cloneNestedKeySet(this.dependencyCache),
      dependencyFingerprints: cloneNestedMap(this.dependencyFingerprints),
    };
  }

  public restoreBuildStateSnapshot(
    snapshot: ProductBuildStateSnapshot<
      SettingsType,
      BlueprintType,
      PartDefinitionType
    >,
  ): void {
    this.settings = snapshot.settings;
    this.blueprint = snapshot.blueprint;
    this.partDefinitions = snapshot.partDefinitions;
    restoreDependencyCache(this.dependencyCache, snapshot.dependencyCacheKeys);
    restoreNestedMap(
      this.dependencyFingerprints,
      snapshot.dependencyFingerprints,
    );
  }

  /**
   * Add a buildstep to the product
   */
  public addBuildStep(
    buildStep: AbstractBuildstep<
      BlueprintType,
      SettingsType,
      PartDefinitionType
    >,
  ): void {
    this.buildSteps.push(buildStep);
  }

  /**
   * Add a part interaction to the product
   */
  public addPartInteraction(
    interaction: AbstractPartInteraction<
      BlueprintType,
      SettingsType,
      PartDefinitionType
    >,
  ): void {
    this.partInteractions.push(interaction);
  }

  /**
   * Generate the blueprint for this product based on settings
   */
  protected abstract generateBlueprint(settings: SettingsType): BlueprintType;

  /**
   * Update an existing blueprint with new settings
   */
  protected abstract updateBlueprint(
    settings: SettingsType,
    blueprint: BlueprintType,
    changes: Partial<SettingsType>,
  ): BlueprintType;

  /**
   * Build the product by generating the blueprint, running all build steps,
   * and then applying part interactions.
   */
  public async build(): Promise<void> {
    const blueprint = this.generateBlueprint(this.settings);
    if (!blueprint) {
      throw new Error("Blueprint generation failed.");
    }

    const newPartDefinitions = new Map<string, PartDefinitionType>();
    const allGeneratedPartIds = new Set<string>();
    this.applyAllBuildSteps(blueprint, newPartDefinitions, allGeneratedPartIds);

    const partsArrayForInteractions = Array.from(newPartDefinitions.values());
    const totalModifications = await this.runAllPartInteractions(
      blueprint,
      partsArrayForInteractions,
    );
    if (totalModifications > 0) {
      this.refreshPartDefinitionsFromInteractions(
        partsArrayForInteractions,
        newPartDefinitions,
      );
    }

    this.blueprint = blueprint;
    this.partDefinitions = newPartDefinitions;
  }

  private applyAllBuildSteps(
    blueprint: BlueprintType,
    newPartDefinitions: Map<string, PartDefinitionType>,
    allGeneratedPartIds: Set<string>,
  ): void {
    for (const step of this.buildSteps) {
      if (!step.selectDependencies || !step.createPart) {
        continue;
      }
      this.applySingleBuildStep(
        blueprint,
        step,
        newPartDefinitions,
        allGeneratedPartIds,
      );
    }
  }

  private applySingleBuildStep(
    blueprint: BlueprintType,
    step: AbstractBuildstep<BlueprintType, SettingsType, PartDefinitionType>,
    newPartDefinitions: Map<string, PartDefinitionType>,
    allGeneratedPartIds: Set<string>,
  ): void {
    const newDependencies = step.selectDependencies(blueprint, this.settings);
    const oldDependencies = this.dependencyCache.get(step.buildstepName);
    const oldFpByPart = this.dependencyFingerprints.get(step.buildstepName);

    const { partsToCreateOrUpdate, nextFpByPart } =
      this.collectPartsToUpdateAndFingerprints(
        newDependencies,
        oldDependencies,
        oldFpByPart,
        allGeneratedPartIds,
      );

    const currentStepPartIds = new Set<string>(newDependencies.keys());
    for (const id of allGeneratedPartIds) {
      currentStepPartIds.add(id);
    }

    const createPart = step.createPart?.bind(step);
    if (createPart === undefined) {
      return;
    }

    this.instantiateUpdatedParts(
      createPart,
      partsToCreateOrUpdate,
      newPartDefinitions,
    );
    this.preserveUnchangedPartsFromCache(
      oldDependencies,
      currentStepPartIds,
      partsToCreateOrUpdate,
      newPartDefinitions,
      this.partInteractions.length > 0,
    );

    this.dependencyCache.set(step.buildstepName, newDependencies);
    this.dependencyFingerprints.set(step.buildstepName, nextFpByPart);
  }

  private collectPartsToUpdateAndFingerprints(
    newDependencies: Map<string, unknown>,
    oldDependencies: Map<string, unknown> | undefined,
    oldFpByPart: Map<string, string> | undefined,
    allGeneratedPartIds: Set<string>,
  ): {
    partsToCreateOrUpdate: Map<string, unknown>;
    nextFpByPart: Map<string, string>;
  } {
    const partsToCreateOrUpdate = new Map<string, unknown>();
    const nextFpByPart = new Map<string, string>();

    for (const [partId, newDep] of newDependencies.entries()) {
      allGeneratedPartIds.add(partId);
      const oldDep = oldDependencies?.get(partId);
      const oldFp = oldFpByPart?.get(partId);

      const newFp = stableFingerprint(newDep);
      nextFpByPart.set(partId, newFp);

      // Compare fingerprints, not object references. Cached deps keep live mutable
      // objects (e.g. Vector3); in-place drag updates reuse the same references.
      if (
        this.isPianoHingePartId(partId) ||
        !oldDep ||
        !oldFp ||
        oldFp !== newFp
      ) {
        partsToCreateOrUpdate.set(partId, newDep);
      }
    }

    return { partsToCreateOrUpdate, nextFpByPart };
  }

  private isPianoHingePartId(partId: string): boolean {
    return (
      typeof partId === "string" && partId.toLowerCase().includes("pianohinge")
    );
  }

  private instantiateUpdatedParts(
    createPart: AbstractBuildstep<
      BlueprintType,
      SettingsType,
      PartDefinitionType
    >["createPart"],
    partsToCreateOrUpdate: Map<string, unknown>,
    newPartDefinitions: Map<string, PartDefinitionType>,
  ): void {
    for (const [partId, depData] of partsToCreateOrUpdate.entries()) {
      const newPart = createPart(partId, depData);
      newPart.id = newPart.id || partId;
      newPartDefinitions.set(partId, newPart);
    }
  }

  private preserveUnchangedPartsFromCache(
    oldDependencies: Map<string, unknown> | undefined,
    currentStepPartIds: Set<string>,
    partsToCreateOrUpdate: Map<string, unknown>,
    newPartDefinitions: Map<string, PartDefinitionType>,
    clonePreservedParts: boolean,
  ): void {
    if (!oldDependencies) {
      return;
    }

    for (const partId of oldDependencies.keys()) {
      if (
        !this.isPreservableCachedPart(
          partId,
          currentStepPartIds,
          partsToCreateOrUpdate,
        )
      ) {
        continue;
      }
      const part = this.partDefinitions.get(partId);
      if (part) {
        newPartDefinitions.set(
          partId,
          clonePreservedParts ? cloneDeep(part) : part,
        );
      }
    }
  }

  private isPreservableCachedPart(
    partId: string,
    currentStepPartIds: Set<string>,
    partsToCreateOrUpdate: Map<string, unknown>,
  ): boolean {
    return (
      currentStepPartIds.has(partId) &&
      !partsToCreateOrUpdate.has(partId) &&
      this.partDefinitions.has(partId)
    );
  }

  private async runAllPartInteractions(
    blueprint: BlueprintType,
    partsArrayForInteractions: PartDefinitionType[],
  ): Promise<number> {
    let totalModifications = 0;
    for (const interaction of this.partInteractions) {
      totalModifications += await this.safeExecuteInteraction(
        blueprint,
        interaction,
        partsArrayForInteractions,
      );
    }
    return totalModifications;
  }

  private async safeExecuteInteraction(
    blueprint: BlueprintType,
    interaction: AbstractPartInteraction<
      BlueprintType,
      SettingsType,
      PartDefinitionType
    >,
    parts: PartDefinitionType[],
  ): Promise<number> {
    try {
      const modifiedCount = await interaction.execute(
        blueprint,
        this.settings,
        parts,
      );
      if (
        typeof modifiedCount !== "number" ||
        !Number.isFinite(modifiedCount) ||
        modifiedCount <= 0
      ) {
        return 0;
      }
      return modifiedCount;
    } catch (error) {
      console.error(
        `Error in interaction '${interaction.interactionName}':`,
        error,
      );
      return 0;
    }
  }

  private refreshPartDefinitionsFromInteractions(
    partsArrayForInteractions: PartDefinitionType[],
    partDefinitions: Map<string, PartDefinitionType>,
  ): void {
    partDefinitions.clear();
    for (const part of partsArrayForInteractions) {
      if (!part.id) {
        continue;
      }
      partDefinitions.set(part.id, part);
    }
    this.cloneCutoutArraysForReact(partDefinitions);
  }

  private cloneCutoutArraysForReact(
    partDefinitions: Map<string, PartDefinitionType>,
  ): void {
    for (const part of partDefinitions.values()) {
      if (!this.partHasNonEmptyCutouts(part)) {
        continue;
      }
      part.cutouts = [...part.cutouts];
    }
  }

  private partHasNonEmptyCutouts(
    part: PartDefinitionType,
  ): part is PartDefinitionType & { cutouts: unknown[] } {
    return (
      "cutouts" in part &&
      Array.isArray(part.cutouts) &&
      part.cutouts.length > 0
    );
  }

  /**
   * Update the product with new settings
   */
  public async update(changes: Partial<SettingsType>): Promise<void> {
    // Update settings with changes
    this.settings = { ...this.settings, ...changes };

    // Update blueprint using blueprint update method
    if (this.blueprint) {
      this.blueprint = this.updateBlueprint(
        this.settings,
        this.blueprint,
        changes,
      );
    } else {
      // If no previous blueprint exists, do a full generation
      this.blueprint = this.generateBlueprint(this.settings);
    }

    // Rebuild parts with updated settings/blueprint
    await this.build();
  }

  /**
   * Post-build settings normalization hook.
   *
   * Called by the build pipeline right after a successful `build()`. Products
   * may inspect the freshly built part definitions and return a settings patch
   * that corrects now-invalid values (e.g. an addable's margin that fell outside
   * the allowed drag area because the case was resized, its alignment switched,
   * or its count/arrangement changed). The pipeline applies the patch and
   * rebuilds once so the correction is reflected immediately. The patch MUST be
   * idempotent — re-normalizing after applying it has to yield no further
   * changes — otherwise the rebuild loop would not terminate.
   *
   * Returns `null` (default) when no correction is needed.
   */
  public normalizeAfterBuild(): Partial<SettingsType> | null {
    return null;
  }

  /**
   * Handle a drag update for a part.
   * Finds the appropriate buildstep and calls its updateSettingsFromDrag method.
   *
   * @param partId - The ID of the dragged part
   * @param newPosition - The new world position after drag
   * @param constraint - The drag constraint from the part definition
   * @param settingsContext - The serializable context data from the part definition
   * @returns DragUpdateResult with settings and optional display values, or null if no update needed
   */
  public handleDragUpdate(
    partId: string,
    newPosition: Vector3,
    constraint: DragConstraint,
    settingsContext: Record<string, unknown> | undefined,
    options?: DragUpdateOptions,
  ): DragUpdateResult<SettingsType> | null {
    // Find the part to get its buildstep name
    const part = this.partDefinitions.get(partId);
    if (!part) {
      console.warn(`Part ${partId} not found for drag update`);
      return null;
    }

    // Find the buildstep that created this part
    const buildstep = this.buildSteps.find(
      (step) => step.buildstepName === part.buildstepName,
    );

    if (!buildstep) {
      console.warn(`Buildstep ${part.buildstepName} not found for drag update`);
      return null;
    }

    // Check if the buildstep supports drag updates
    if (!buildstep.updateSettingsFromDrag) {
      console.warn(
        `Buildstep ${part.buildstepName} does not support drag updates`,
      );
      return null;
    }

    // Call the buildstep's update method
    return buildstep.updateSettingsFromDrag(
      partId,
      newPosition,
      constraint,
      settingsContext,
      this.settings,
      options,
    );
  }

  /**
   * Serialize the product definition.
   */
  public abstract serialize(): unknown;

  /**
   * Returns the variable options available for this product.
   * @param optionDb The option database to use for creating variable options
   * @param cuts The cuts to use for creating variable options
   * @returns An object containing variable options for the product.
   */
  public abstract getAllowedVariableOptions(
    optionDb: unknown,
    cuts: unknown[],
  ): {
    [key: string]: () => unknown;
  };

  /**
   * Returns downloadable files for this product (e.g., DXF files for CNC).
   * Override this method in subclasses to provide product-specific downloads.
   *
   * @param context - Optional context data (e.g. price data) for download generation
   * @returns Promise resolving to an array of Download objects
   */
  public async getDownloads(
    _context?: Record<string, unknown>,
  ): Promise<Download[]> {
    return [];
  }
}
